"use client";

import { useEffect, useMemo, useState } from "react";
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from "pdf-lib";

type OperatorOption = { name: string; pharmacy: string };
type PerfadomProduct = {
  productName: string;
  volume: "50 ml" | "100 ml" | "250 ml" | "500 ml" | "1000 ml";
  moment: string;
  schedule: "daily" | "every2A" | "every2B" | "manual";
  description: string;
  durationHours: string;
  durationMinutes: string;
  route: "central" | "peripheral" | "subcutaneous" | "perineural";
  centralType: "implantable" | "central" | "picc";
  mode: "gravity" | "diffuser" | "electric";
  electricType: "ambulatory" | "fixed";
  hospitalFilled: boolean;
  totalPerfusions: string;
  frequencyCount: string;
  frequencyUnit: "day" | "week" | "month";
  startDate: string;
  endDate: string;
  durationDays: string;
  maintenance: boolean;
  maintenancePicc: boolean;
  transfuser: boolean;
  alone: boolean;
};

type PerfadomForm = {
  reference: string;
  pharmacy: string;
  operator: string;
  prescriptionDate: string;
  prescriptionType: "initiation" | "renewal";
  patientLastName: string;
  patientFirstName: string;
  birthDate: string;
  weight: string;
  insuredNumber: string;
  ald: boolean;
  prescriberLastName: string;
  prescriberFirstName: string;
  prescriberPhone: string;
  rpps: string;
  structure: string;
  structureAddress: string;
  finess: string;
  assuranceMaladie: string;
  careSetting: "city" | "had";
  ideName: string;
  ideEmail: string;
  idePhone: string;
  ideAddress: string;
  installation: boolean;
  hospitalPrepared: boolean;
  catheterType: "blue" | "yellow";
  threeWayExtension: boolean;
  recipientPatient: boolean;
  recipientProducts: boolean;
  recipientServices: boolean;
  recipientIde: boolean;
  recipientHospital: boolean;
  comments: string;
  priorCure26Weeks: "yes" | "no";
  currentCure: "yes" | "no";
  products: PerfadomProduct[];
  allocatePump: boolean;
  equipmentId: number;
};

type MadEquipment = {
  id: number;
  name: string;
  category: string;
  pharmacy: string;
  inventoryCode: string;
  serialNumber: string;
  status: string;
};

type TrackingItem = {
  id: number;
  reference: string;
  pharmacy: string;
  operator: string;
  status: "draft" | "active" | "closed";
  startDate: string;
  endDate: string;
  administrationMode: string;
  weeklyPerfusions: number;
  ideName: string;
  ideEmail: string;
  reservedEquipmentId: number;
  patientName: string;
  prescriptionDate: string;
  durationDays: number;
  payload: PerfadomForm | null;
  invoiceStatus: Record<string, boolean>;
  updatedAt: number;
};

type BillingLine = { code: string; label: string; amount: number; frequency: string };
type MaterialLine = { qty: number; title: string; note: string; tag: string; lgpi?: string };

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDaysIso = (date: string, days: number) => {
  const value = new Date(`${date || todayIso()}T12:00:00`);
  value.setDate(value.getDate() + days);
  return value.toISOString().slice(0, 10);
};
const formatDate = (value: string) => value ? value.split("-").reverse().join("/") : "";
const emptyProduct = (): PerfadomProduct => ({
  productName: "NaCl", volume: "500 ml", moment: "", schedule: "daily",
  description: "", durationHours: "12", durationMinutes: "", route: "subcutaneous", centralType: "implantable",
  mode: "diffuser", electricType: "ambulatory", hospitalFilled: false, totalPerfusions: "", frequencyCount: "1",
  frequencyUnit: "day", startDate: todayIso(), endDate: addDaysIso(todayIso(), 6), durationDays: "7",
  maintenance: false, maintenancePicc: false, transfuser: false, alone: false,
});
const blankForm = (pharmacy: string, operator: string): PerfadomForm => ({
  reference: `PERF-${Date.now().toString(36).toUpperCase()}`,
  pharmacy, operator, prescriptionDate: todayIso(), prescriptionType: "initiation",
  patientLastName: "", patientFirstName: "", birthDate: "", weight: "", insuredNumber: "", ald: false,
  prescriberLastName: "", prescriberFirstName: "", prescriberPhone: "", rpps: "", structure: "",
  structureAddress: "", finess: "", assuranceMaladie: "", careSetting: "city",
  ideName: "", ideEmail: "", idePhone: "", ideAddress: "", installation: true, hospitalPrepared: false,
  catheterType: "blue", threeWayExtension: false,
  recipientPatient: false, recipientProducts: true, recipientServices: true, recipientIde: false, recipientHospital: false,
  comments: "", priorCure26Weeks: "no", currentCure: "no",
  products: [emptyProduct()], allocatePump: false, equipmentId: 0,
});

function weeklyCount(product: PerfadomProduct) {
  if (product.schedule === "daily") return 7;
  if (product.schedule === "every2A") return 4;
  if (product.schedule === "every2B") return 3;
  const count = Math.max(0, Number(product.frequencyCount) || 0);
  if (product.frequencyUnit === "day") return count * 7;
  if (product.frequencyUnit === "month") return Math.ceil(count / 4);
  return count;
}

function cerfaCureDays(product: PerfadomProduct) {
  const calculated = Math.round((new Date(`${product.endDate}T12:00:00`).getTime() - new Date(`${product.startDate}T12:00:00`).getTime()) / 86_400_000) + 1;
  return Math.max(1, Number(product.durationDays) || calculated || 1);
}

function cerfaTotalPerfusions(product: PerfadomProduct) {
  const days = cerfaCureDays(product);
  if (product.totalPerfusions) return product.totalPerfusions;
  if (product.schedule === "daily") return String(days);
  if (product.schedule === "every2A" || product.schedule === "every2B") return String(Math.ceil(days / 2));
  return "";
}

function cerfaFrequency(product: PerfadomProduct) {
  if (product.schedule === "daily") return "1 perfusion par jour";
  if (product.schedule === "every2A" || product.schedule === "every2B") return "1 perfusion tous les 2 jours";
  const unit = product.frequencyUnit === "day" ? "jour" : product.frequencyUnit === "week" ? "semaine" : "mois";
  return `${product.frequencyCount || 1} perfusion(s) par ${unit}`;
}

function activeProductModes(products: PerfadomProduct[]) {
  return products.filter((item) => item.productName?.trim() || item.description.trim()).map((item) => item.mode);
}

function activeModeLabel(products: PerfadomProduct[]) {
  const labels = Array.from(new Set(activeProductModes(products))).map((mode) =>
    mode === "electric" ? "Système actif" : mode === "diffuser" ? "Diffuseur" : "Gravité",
  );
  return labels.join(" + ");
}

function consumableFor(mode: PerfadomProduct["mode"], count: number): BillingLine[] {
  if (!count) return [];
  if (mode === "gravity") {
    if (count < 7) return [{ code: "F17", label: `${count} perfusion${count > 1 ? "s" : ""} par gravité`, amount: count * 10.8, frequency: "par perfusion" }];
    if (count < 14) return [
      { code: "F18", label: "Socle consommables gravité - 7 perfusions", amount: 76.02, frequency: "hebdomadaire" },
      ...(count > 7 ? [{ code: "F17", label: `${count - 7} perfusion${count - 7 > 1 ? "s" : ""} complémentaire${count - 7 > 1 ? "s" : ""}`, amount: (count - 7) * 10.8, frequency: "par perfusion" }] : []),
    ];
    if (count < 21) return [
      { code: "F19", label: "Socle consommables gravité - 14 perfusions", amount: 143.8, frequency: "hebdomadaire" },
      ...(count > 14 ? [{ code: "F17", label: `${count - 14} perfusion${count - 14 > 1 ? "s" : ""} complémentaire${count - 14 > 1 ? "s" : ""}`, amount: (count - 14) * 10.8, frequency: "par perfusion" }] : []),
    ];
    return [{ code: "F20", label: "Consommables gravité - 21 perfusions ou plus", amount: 204.24, frequency: "hebdomadaire" }];
  }
  const isElectric = mode === "electric";
  const bands = isElectric
    ? [
        [1, 1, "F27", 34.76], [2, 3, "F28", 69.51], [4, 6, "F29", 156.4],
        [7, 7, "F30", 240.15], [14, 14, "F31", 454.97], [21, 21, "F32", 647.27], [28, 999, "F33", 815.18],
      ] as const
    : [
        [1, 1, "F34", 31.27], [2, 3, "F35", 62.55], [4, 6, "F36", 140.75],
        [7, 7, "F37", 216.12], [14, 14, "F38", 409.47], [21, 21, "F39", 582.54], [28, 999, "F40", 733.66],
      ] as const;
  const exact = bands.find(([min, max]) => count >= min && count <= max);
  if (exact) return [{ code: exact[2], label: `${count} perfusion${count > 1 ? "s" : ""} - ${isElectric ? "système actif" : "diffuseur"}`, amount: exact[3], frequency: "hebdomadaire" }];
  const base = count > 21 ? bands[5] : count > 14 ? bands[4] : count > 7 ? bands[3] : bands[0];
  const remainder = count - base[0];
  return [
    { code: base[2], label: `Socle ${base[0]} perfusions - ${isElectric ? "système actif" : "diffuseur"}`, amount: base[3], frequency: "hebdomadaire" },
    ...consumableFor(mode, remainder),
  ];
}

function calculateBilling(products: PerfadomProduct[], form?: PerfadomForm) {
  const active = products.filter((item) => item.productName?.trim() || item.description.trim());
  const modes = Array.from(new Set(active.map((item) => item.mode)));
  const installationCandidates: BillingLine[] = [];
  if (modes.includes("electric")) {
    const hospitalFilled = active.filter((item) => item.mode === "electric").every((item) => item.hospitalFilled);
    installationCandidates.push(hospitalFilled
      ? { code: "F3", label: "Installation système actif rempli/posé à l’hôpital", amount: 164.86, frequency: "une fois" }
      : { code: "F1", label: "Installation système actif électrique", amount: 357.2, frequency: "une fois" });
  }
  if (modes.includes("diffuser") && active.some((item) => item.mode === "diffuser" && !item.hospitalFilled)) {
    installationCandidates.push({ code: "F4", label: "Installation diffuseur", amount: 228.97, frequency: "une fois" });
  }
  if (modes.includes("gravity")) installationCandidates.push({ code: "F6", label: "Installation + suivi gravité", amount: 45.79, frequency: "une fois" });
  const installation = form?.installation === false ? [] : installationCandidates.sort((a, b) => b.amount - a.amount).slice(0, 1);
  const followupCandidates: BillingLine[] = [];
  if (modes.includes("electric")) followupCandidates.push({ code: "F7", label: "Suivi système actif électrique", amount: 100.75, frequency: "hebdomadaire" });
  if (modes.includes("diffuser") && active.some((item) => item.mode === "diffuser" && !item.hospitalFilled)) {
    followupCandidates.push({ code: "F8", label: "Suivi diffuseur", amount: 45.79, frequency: "hebdomadaire" });
  }
  const followup = followupCandidates.sort((a, b) => b.amount - a.amount).slice(0, 1);
  const consumables = modes.flatMap((mode) => consumableFor(
    mode,
    active.filter((item) => item.mode === mode && !(mode === "diffuser" && item.hospitalFilled)).reduce((sum, item) => sum + weeklyCount(item), 0),
  ));
  return { installation, followup, consumables, act: [] as BillingLine[], all: [...installation, ...followup, ...consumables] };
}

function productTotalAdministrations(product: PerfadomProduct) {
  const explicit = Math.max(0, Number(product.totalPerfusions) || 0);
  if (explicit) return Math.ceil(explicit);
  const days = Math.max(1, Number(product.durationDays) || cerfaCureDays(product));
  if (product.schedule === "daily") return days;
  if (product.schedule === "every2A" || product.schedule === "every2B") return Math.ceil(days / 2);
  const frequency = Math.max(1, Number(product.frequencyCount) || 1);
  if (product.frequencyUnit === "day") return Math.ceil(days * frequency);
  if (product.frequencyUnit === "month") return Math.ceil(days / 30) * frequency;
  return Math.ceil(days / 7) * frequency;
}

function calculateMaterials(products: PerfadomProduct[], form: PerfadomForm): MaterialLine[] {
  const active = products.filter((item) => item.productName?.trim() || item.description.trim());
  if (!active.length) return [];
  const lines: MaterialLine[] = [];
  const add = (qty: number, title: string, note: string, tag: string, lgpi?: string) => {
    if (qty > 0) lines.push({ qty: Math.ceil(qty), title, note, tag, lgpi });
  };
  const totalDays = Math.max(...active.map((item) => Math.max(1, Number(item.durationDays) || cerfaCureDays(item))));
  const diffuserGroups = new Map<string, { qty: number; volume: string; duration: string }>();

  active.filter((item) => item.mode === "diffuser").forEach((product) => {
    const duration = product.durationHours
      ? `${product.durationHours} h${product.durationMinutes ? ` ${product.durationMinutes} min` : ""}`
      : "durée à préciser";
    const key = `${product.volume}|${duration}`;
    const current = diffuserGroups.get(key) ?? { qty: 0, volume: product.volume, duration };
    current.qty += productTotalAdministrations(product);
    diffuserGroups.set(key, current);
  });

  diffuserGroups.forEach((group) => add(
    group.qty,
    `Diffuseur ${group.volume} — ${group.duration}`,
    `1 diffuseur par administration prescrite · volume et durée à respecter`,
    "obligatoire",
    `KIT DIFFUSEUR ANDRONE ${group.volume} / ${group.duration}`,
  ));

  const diffuserProducts = active.filter((item) => item.mode === "diffuser");
  const totalDiffusers = diffuserProducts.reduce((sum, item) => sum + productTotalAdministrations(item), 0);
  const total500Ml = diffuserProducts
    .filter((item) => item.volume.replace(/\s+/g, " ").trim() === "500 ml")
    .reduce((sum, item) => sum + productTotalAdministrations(item), 0);
  const electricCount = active.filter((item) => item.mode === "electric").reduce((sum, item) => sum + productTotalAdministrations(item), 0);

  if (form.allocatePump && total500Ml > 0) add(total500Ml, "Set de transfert Androne", "Uniquement pour les diffuseurs Androne 500 ml · 1 set par diffuseur", "500 ml uniquement");
  if (totalDiffusers > 0) {
    if (!form.allocatePump) add(totalDiffusers, "Seringue 50 ou 60 ml", "Pour remplir le diffuseur sans pousse-seringue Androne", "obligatoire", "SERINGUE OMNIFIX SER LOCK 50 OU 60 ML");
    add(1, "Sacoche banane diffuseur", "Pour porter le diffuseur pendant tout le traitement", "traitement");
  }
  if (form.allocatePump) add(totalDiffusers + electricCount, "Seringue Androne 20 ml Luer Lock", "1 seringue par administration avec le pousse-seringue Androne", "obligatoire Androne");

  if (electricCount > 0) add(electricCount, "Set de perfusion / tubulure système actif", "À adapter à la pompe et au protocole", "à vérifier");
  const gravityCount = active.filter((item) => item.mode === "gravity").reduce((sum, item) => sum + productTotalAdministrations(item), 0);
  if (gravityCount > 0) add(gravityCount, "Tubulure gravité / nécessaire de perfusion", "1 par perfusion selon protocole", "obligatoire");
  if (form.allocatePump) add(1, "Pompe Androne", "Pompe attribuée explicitement à l’IDE / dossier patient", "attribuée");

  const catheterCount = Math.max(1, Math.ceil(totalDays / 3));
  add(
    catheterCount,
    form.catheterType === "yellow" ? "Cathéter jaune avec prolongateur intégré" : "Cathéter bleu",
    "Renouvellement environ tous les 3 jours",
    "obligatoire",
    form.catheterType === "yellow" ? "CATHETER IV ANDERMED" : "INTROSCAN COURT SAFETY W22G 25MM 3540",
  );
  if (form.catheterType === "blue") add(catheterCount, "Prolongateur de cathéter", "À ajouter avec chaque cathéter bleu", "obligatoire", "PROLONG BRAUN 0,10 + ROB 3V");
  const dressingBoxes = Math.max(1, Math.ceil(catheterCount / 5));
  add(dressingBoxes, "Boîte de pansements film 10 × 15", `${catheterCount} pansement${catheterCount > 1 ? "s" : ""} nécessaire${catheterCount > 1 ? "s" : ""} · 5 pansements par boîte`, "boîte de 5");
  add(Math.max(1, Math.ceil(totalDays / 30)), "Boîte de compresses", "Repère pratique : 1 boîte par mois", "mensuel");
  add(1, "Antiseptique type Biseptine", "Pour l’ensemble du traitement, selon protocole infirmier", "obligatoire");
  add(1, "Gants non stériles", "Selon protocole et besoin de l’IDE", "optionnel");
  if (form.threeWayExtension) add(catheterCount, "Prolongateur robinet 3 voies", "Si besoin de longueur supplémentaire", "optionnel");
  return lines;
}

function parseJson<T>(value: unknown, fallback: T): T {
  if (value && typeof value === "object") return value as T;
  try { return typeof value === "string" ? JSON.parse(value) as T : fallback; } catch { return fallback; }
}

function dossierWeeks(startDate: string, endDate: string) {
  if (!startDate || !endDate) return [];
  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];
  const result: string[] = [];
  for (let cursor = new Date(start); cursor <= end && result.length < 156; cursor.setDate(cursor.getDate() + 7)) result.push(cursor.toISOString().slice(0, 10));
  return result;
}

function splitText(font: PDFFont, text: string, size: number, maxWidth: number) {
  const words = text.replace(/\s+/g, " ").trim().split(" ");
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) line = candidate;
    else { if (line) lines.push(line); line = word; }
  }
  if (line) lines.push(line);
  return lines;
}

function drawCheck(page: PDFPage, font: PDFFont, x: number, y: number, checked: boolean, label: string, size = 7) {
  page.drawRectangle({ x, y, width: 8, height: 8, borderWidth: .7, borderColor: rgb(.18, .22, .25) });
  if (checked) page.drawText("X", { x: x + 1.2, y: y + .6, size: 7, font, color: rgb(.02, .39, .33) });
  page.drawText(label, { x: x + 11, y: y + .8, size, font, color: rgb(.08, .1, .12) });
}

function drawTextBox(page: PDFPage, font: PDFFont, label: string, value: string, x: number, y: number, width: number, height = 25) {
  page.drawText(label.toUpperCase(), { x, y: y + height - 7, size: 5.5, font, color: rgb(.32, .37, .41) });
  page.drawRectangle({ x, y, width, height: height - 9, borderWidth: .5, borderColor: rgb(.7, .73, .74) });
  const safe = value || "";
  page.drawText(safe.slice(0, 80), { x: x + 4, y: y + 4, size: 8.2, font, color: rgb(.04, .06, .09) });
}

export async function buildPerfadomPdf(form: PerfadomForm) {
  const response = await fetch("/docs/formulaire/perfusion-domicile-perfadom-ordo.pdf");
  if (!response.ok) throw new Error("PDF PERFADOM officiel introuvable");
  const pdf = await PDFDocument.load(await response.arrayBuffer());
  const normal = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const page = pdf.getPages()[0];
  const black = rgb(0, 0, 0);
  const clean = (value: unknown) => String(value ?? "").replace(/\s+/g, " ").trim();
  const wrap = (value: unknown, max = 60) => {
    const words = clean(value).split(" ").filter(Boolean); const lines: string[] = []; let line = "";
    words.forEach((word) => { if (`${line} ${word}`.trim().length > max) { if (line) lines.push(line); line = word; } else line = `${line} ${word}`.trim(); });
    if (line) lines.push(line); return lines;
  };
  const check = (x: number, y: number, enabled = true, size = 6.4) => {
    if (!enabled) return; const width = bold.widthOfTextAtSize("X", size);
    page.drawText("X", { x: x - width / 2, y: y - size * .36, size, font: bold, color: black });
  };
  const text = (value: unknown, x: number, y: number, size = 7, max = 60, lines = 5, font = normal) => {
    wrap(value, max).slice(0, lines).forEach((line, index) => page.drawText(line, { x, y: y - index * (size + 2), size, font, color: black }));
  };
  const charsAt = (value: unknown, xs: number[], y: number, size = 6.2, digitsOnly = false) => {
    const source = digitsOnly ? clean(value).replace(/\D+/g, "") : clean(value);
    source.split("").slice(0, xs.length).forEach((character, index) => {
      const width = normal.widthOfTextAtSize(character, size);
      page.drawText(character, { x: xs[index] - width / 2, y: y - size * .36, size, font: normal, color: black });
    });
  };
  const dateDigits = (value: string) => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/); return match ? `${match[3]}${match[2]}${match[1]}` : value.replace(/\D+/g, "").slice(0, 8);
  };
  const dateAt = (value: string, xs: number[], y: number, size = 6.1) => charsAt(dateDigits(value), xs, y, size);
  const route = (product: PerfadomProduct, block: 1 | 2) => {
    const p = block === 2 ? { main:[364.04,374.44], ch:[379.94,362.82], cc:[379.94,351.62], picc:[379.94,340.56], peri:[364.04,320.67], vp:[364.04,308.12], sc:[364.04,296.09] } : { main:[362.31,543.05], ch:[378.21,531.44], cc:[378.21,520.24], picc:[378.21,509.15], peri:[362.31,489.27], vp:[362.31,476.92], sc:[362.31,464.69] };
    if (product.route === "central") { check(...p.main); check(...(product.centralType === "implantable" ? p.ch : product.centralType === "picc" ? p.picc : p.cc)); }
    else if (product.route === "perineural") check(...p.peri); else if (product.route === "subcutaneous") check(...p.sc); else check(...p.vp);
  };
  const mode = (product: PerfadomProduct, block: 1 | 2) => {
    const p = block === 2 ? { g:[465.36,374.44], d:[465.36,362.21], sa:[465.36,349.87], amb:[481.26,338.25], fixed:[481.26,327.18] } : { g:[464.44,543.05], d:[464.44,530.82], sa:[464.44,518.47], amb:[480.34,506.84], fixed:[480.34,495.77] };
    if (product.mode === "gravity") check(...p.g); else if (product.mode === "diffuser") check(...p.d); else { check(...p.sa); check(...(product.electricType === "fixed" ? p.fixed : p.amb)); }
  };
  const productBlock = (product: PerfadomProduct, block: 1 | 2) => {
    const second = block === 2;
    const base = second ? { prodY:344, totalY:266.2, dateY:238.97, start:[53.45,60.85,73.72,81.11,93.88,101.27,108.62,116.02], end:[169.31,176.71,189.58,196.98,209.74,217.14,224.49,231.89], cureX:280, alone:[533.94,239.52] } : { prodY:512, totalY:434.8, dateY:407, start:[53.45,60.85,73.72,81.11,93.88,101.27,108.62,116.02], end:[170.35,177.75,190.62,198.02,210.78,218.18,225.53,232.92], cureX:282, alone:[533.02,407.55] };
    const productText = [product.productName, product.volume, product.description, product.moment].filter(Boolean).join(" — ");
    text(productText, 48, base.prodY, 6.5, 72, 5);
    const duration = `${product.durationHours || ""}${product.durationMinutes ? ` h ${product.durationMinutes} min` : ""}`;
    const durationPoint = second ? { h:[181.5,288.2], m:[245.5,288.2] } : { h:[181.5,456.8], m:[245.5,456.8] };
    if (product.durationMinutes && !product.durationHours) text(product.durationMinutes, ...durationPoint.m, 7.2, 6, 1); else text(product.durationHours || duration, ...durationPoint.h, 7.2, 6, 1);
    const cureDays = Math.max(1, Number(product.durationDays) || Math.round((new Date(`${product.endDate}T12:00:00`).getTime() - new Date(`${product.startDate}T12:00:00`).getTime()) / 86_400_000) + 1 || 1);
    const computedTotal = product.schedule === "every2A" || product.schedule === "every2B" ? Math.ceil(cureDays / 2) : product.schedule === "daily" ? cureDays : Number(product.totalPerfusions) || 0;
    text(product.totalPerfusions || computedTotal, 140, base.totalY, 7.2, 6, 1);
    const frequencyCount = product.schedule === "daily" ? "1" : product.schedule === "every2A" || product.schedule === "every2B" ? "1/2" : product.frequencyCount || "1";
    const fp = second ? { n:[228.8,260.3], day:[291.72,279.18], week:[291.72,270.99], month:[291.72,262.63] } : { n:[229.8,429.0], day:[293.80,447.77], week:[293.80,439.58], month:[293.80,431.24] };
    text(frequencyCount, ...fp.n, 7.2, 8, 1); check(...(product.frequencyUnit === "week" ? fp.week : product.frequencyUnit === "month" ? fp.month : fp.day));
    dateAt(product.startDate, base.start, base.dateY); dateAt(product.endDate, base.end, base.dateY); text(product.durationDays || cureDays, base.cureX, base.dateY - 3.6, 7, 5, 1);
    route(product, block); mode(product, block); check(base.alone[0], base.alone[1], product.alone);
  };

  const dateOrdoXs=[107.19,114.55,127.25,134.60,149.01,156.41,163.76,171.15];
  const dateBirthXs=[343.59,350.95,363.65,371.00,383.71,391.11,398.46,405.85];
  const nirXs=[322.72,330.11,337.49,344.85,352.24,359.60,367.02,374.38,381.77,389.15,396.39,403.78,411.16,418.51,425.91];
  const phoneXs=[69.38,76.77,89.44,96.79,109.49,116.84,129.54,136.90,149.47,156.83];
  const rppsXs=[90.94,98.30,105.70,113.05,120.45,127.80,135.06,142.34,149.69,157.09,164.44];
  const finessXs=[353.84,361.21,368.61,375.96,383.24,390.60,397.99,405.34,412.74];
  const amXs=[482.50,489.87,497.26,504.50,511.90,519.25,526.65,534.00,541.40];
  dateAt(form.prescriptionDate || todayIso(), dateOrdoXs, 782.72);
  check(28.71, form.prescriptionType === "renewal" ? 758.47 : 770.84);
  text(form.patientLastName, 298, 780.4, 7.2, 28, 1, bold); text(form.patientFirstName, 471, 780.4, 7.2, 18, 1, bold);
  dateAt(form.birthDate, dateBirthXs, 771.87);
  const weight = form.weight.replace(",", ".").replace(/[^0-9.]/g, "").split("."); charsAt((weight[0] || "").slice(0, 3), [492.18,499.55,506.94], 771.87, 6.1); if (weight[1]) charsAt(weight[1].slice(0,1), [519.81], 771.87, 6.1);
  charsAt(form.insuredNumber, nirXs, 759.62, 6, true); check(441.71, 759.97, form.ald);
  text(form.prescriberLastName, 47, 718.2, 7, 31, 1, bold); text(form.prescriberFirstName, 56, 706, 7, 29, 1);
  charsAt(form.prescriberPhone, phoneXs, 697.21, 6, true); charsAt(form.rpps, rppsXs, 684.98, 5.9, true);
  text(form.structure, 313, 718.2, 6.8, 56, 1); text(form.structureAddress, 294, 706, 6.6, 66, 1);
  charsAt(form.finess, finessXs, 684.98, 5.9, true); charsAt(form.assuranceMaladie, amXs, 684.98, 5.9, true);
  check(69.25, 641.46, form.recipientPatient); check(171.56, 641.46, form.recipientProducts); check(273.69, 641.46, form.recipientServices); check(382.63, 641.46, form.recipientIde); check(524.74, 641.46, form.recipientHospital);
  if (form.products[0]) productBlock(form.products[0], 1); if (form.products[1]?.description.trim()) productBlock(form.products[1], 2);
  text(form.comments, 43, 216, 6.5, 55, 9);
  check(form.priorCure26Weeks === "yes" ? 328.29 : 366.69, 186.70); check(form.currentCure === "yes" ? 328.29 : 366.69, 113.66);
  return pdf.save();
}

export async function buildMaterialsChecklistPdf(form: PerfadomForm, materials: MaterialLine[]) {
  const pdf = await PDFDocument.create();
  const normal = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const pageSize: [number, number] = [595.28, 841.89];
  const margin = 38;
  const green = rgb(.02, .39, .31);
  const ink = rgb(.08, .12, .15);
  const muted = rgb(.36, .42, .45);
  const light = rgb(.93, .97, .95);
  let page = pdf.addPage(pageSize);
  let y = 0;

  const drawHeader = () => {
    page.drawRectangle({ x: 0, y: pageSize[1] - 92, width: pageSize[0], height: 92, color: green });
    page.drawText("CHECKLIST MATÉRIEL PERFADOM", { x: margin, y: pageSize[1] - 45, size: 18, font: bold, color: rgb(1, 1, 1) });
    page.drawText(`${form.reference}  ·  ${form.pharmacy}`, { x: margin, y: pageSize[1] - 67, size: 9, font: normal, color: rgb(.84, .96, .91) });
    const patient = `${form.patientLastName.toUpperCase()} ${form.patientFirstName}`.trim() || "Patient non renseigné";
    page.drawText(`Patient : ${patient}`, { x: margin, y: pageSize[1] - 112, size: 10, font: bold, color: ink });
    page.drawText(`IDE : ${form.ideName || "non renseignée"}  ·  Préparé par : ${form.operator || "non renseigné"}`, { x: margin, y: pageSize[1] - 130, size: 8.5, font: normal, color: muted });
    page.drawRectangle({ x: margin, y: pageSize[1] - 168, width: pageSize[0] - margin * 2, height: 24, color: light });
    page.drawText("QTÉ", { x: margin + 7, y: pageSize[1] - 160, size: 7, font: bold, color: green });
    page.drawText("MATÉRIEL À FOURNIR", { x: margin + 48, y: pageSize[1] - 160, size: 7, font: bold, color: green });
    page.drawText("PRÉPARÉ", { x: pageSize[0] - 128, y: pageSize[1] - 160, size: 7, font: bold, color: green });
    page.drawText("CONTRÔLÉ", { x: pageSize[0] - 77, y: pageSize[1] - 160, size: 7, font: bold, color: green });
    y = pageSize[1] - 183;
  };
  const newPage = () => { page = pdf.addPage(pageSize); drawHeader(); };
  drawHeader();

  materials.forEach((item, index) => {
    const titleLines = splitText(bold, item.title, 9, 300);
    const noteLines = splitText(normal, `${item.note}${item.tag ? ` · ${item.tag}` : ""}${item.lgpi ? ` · LGPI : ${item.lgpi}` : ""}`, 7.5, 300);
    const rowHeight = Math.max(42, 14 + titleLines.length * 11 + noteLines.length * 9);
    if (y - rowHeight < 72) newPage();
    if (index % 2 === 0) page.drawRectangle({ x: margin, y: y - rowHeight + 4, width: pageSize[0] - margin * 2, height: rowHeight, color: rgb(.975, .985, .98) });
    page.drawText(String(item.qty), { x: margin + 9, y: y - 15, size: 12, font: bold, color: green });
    titleLines.forEach((line, lineIndex) => page.drawText(line, { x: margin + 48, y: y - 12 - lineIndex * 11, size: 9, font: bold, color: ink }));
    const noteY = y - 16 - titleLines.length * 11;
    noteLines.forEach((line, lineIndex) => page.drawText(line, { x: margin + 48, y: noteY - lineIndex * 9, size: 7.5, font: normal, color: muted }));
    page.drawRectangle({ x: pageSize[0] - 112, y: y - 22, width: 13, height: 13, borderWidth: 1, borderColor: green });
    page.drawRectangle({ x: pageSize[0] - 59, y: y - 22, width: 13, height: 13, borderWidth: 1, borderColor: green });
    page.drawLine({ start: { x: margin, y: y - rowHeight + 4 }, end: { x: pageSize[0] - margin, y: y - rowHeight + 4 }, thickness: .4, color: rgb(.82, .87, .85) });
    y -= rowHeight;
  });

  if (y < 105) newPage();
  page.drawText("Double vérification manuelle", { x: margin, y: y - 15, size: 9, font: bold, color: green });
  page.drawText("Préparé par : ____________________    Date : ____ / ____ / ______", { x: margin, y: y - 38, size: 9, font: normal, color: ink });
  page.drawText("Contrôlé par : ___________________    Date : ____ / ____ / ______", { x: margin, y: y - 58, size: 9, font: normal, color: ink });
  return pdf.save();
}

function normalizeTracking(row: Record<string, unknown>): TrackingItem {
  const payload = parseJson<PerfadomForm | null>(row.payload ?? row.payload_json, null);
  return {
    id: Number(row.id),
    reference: String(row.reference ?? ""),
    pharmacy: String(row.pharmacy ?? ""),
    operator: String(row.operator ?? ""),
    status: String(row.status) === "closed" ? "closed" : String(row.status) === "active" ? "active" : "draft",
    startDate: String(row.start_date ?? row.startDate ?? ""),
    endDate: String(row.end_date ?? row.endDate ?? ""),
    administrationMode: String(row.administration_mode ?? row.administrationMode ?? ""),
    weeklyPerfusions: Number(row.weekly_perfusions ?? row.weeklyPerfusions ?? 0),
    ideName: String(row.ide_name ?? row.ideName ?? ""),
    ideEmail: String(row.ide_email ?? row.ideEmail ?? ""),
    reservedEquipmentId: Number(row.reserved_equipment_id ?? row.reservedEquipmentId ?? 0),
    patientName: String(row.patient_name ?? row.patientName ?? ""),
    prescriptionDate: String(row.prescription_date ?? row.prescriptionDate ?? ""),
    durationDays: Number(row.duration_days ?? row.durationDays ?? 0),
    payload,
    invoiceStatus: parseJson<Record<string, boolean>>(row.invoiceStatus ?? row.invoice_status_json, {}),
    updatedAt: Number(row.updated_at ?? row.updatedAt ?? 0),
  };
}

function trackingForm(item: TrackingItem, renewal = false): PerfadomForm {
  const base = blankForm(item.pharmacy, item.operator);
  const source = item.payload;
  const products = Array.isArray(source?.products) && source.products.length
    ? source.products.map((product) => ({ ...emptyProduct(), ...product }))
    : [{
      ...emptyProduct(),
      productName: "",
      description: item.administrationMode || "Dossier historique à compléter",
      startDate: item.startDate || todayIso(),
      endDate: item.endDate || addDaysIso(item.startDate || todayIso(), Math.max(1, item.durationDays || 7) - 1),
      durationDays: String(item.durationDays || 7),
      schedule: "manual" as const,
      frequencyCount: String(item.weeklyPerfusions || 1),
      frequencyUnit: "week" as const,
      mode: /actif|électrique/i.test(item.administrationMode) ? "electric" as const : /gravité/i.test(item.administrationMode) ? "gravity" as const : "diffuser" as const,
    }];
  const [patientLastName = "", ...patientFirstName] = item.patientName.trim().split(/\s+/);
  return {
    ...base,
    ...source,
    products,
    reference: renewal ? base.reference : item.reference,
    pharmacy: item.pharmacy,
    operator: item.operator || source?.operator || base.operator,
    patientLastName: source?.patientLastName || patientLastName,
    patientFirstName: source?.patientFirstName || patientFirstName.join(" "),
    prescriptionType: renewal ? "renewal" : source?.prescriptionType || base.prescriptionType,
    prescriptionDate: renewal ? todayIso() : source?.prescriptionDate || item.prescriptionDate || todayIso(),
    allocatePump: renewal ? false : source?.allocatePump ?? false,
    equipmentId: renewal ? 0 : source?.equipmentId ?? 0,
  };
}

function PerfadomTrackingView({
  items, pharmacy, onPharmacy, onOpen, onDuplicate, onUpdate, onDelete,
}: {
  items: TrackingItem[];
  pharmacy: string;
  onPharmacy: (value: string) => void;
  onOpen: (item: TrackingItem) => void;
  onDuplicate: (item: TrackingItem) => void;
  onUpdate: (item: TrackingItem, action: "close" | "reopen" | "toggle_week" | "reassign", extra?: Record<string, unknown>) => Promise<void>;
  onDelete: (item: TrackingItem) => Promise<void>;
}) {
  const today = todayIso();
  const active = items.filter((item) => item.status !== "closed");
  const lateCount = active.filter((item) => dossierWeeks(item.startDate, item.endDate).some((week) => week <= today && !item.invoiceStatus[week])).length;
  return <section className="perfadom-v50-tracking">
    <header>
      <div><span>SUIVI PARTAGÉ</span><h2>Mes dossiers PERFADOM</h2><p>Facturation semaine par semaine, matériel et échéances.</p></div>
      <label>Pharmacie <select value={pharmacy} onChange={(event) => onPharmacy(event.target.value)}><option>Toutes</option><option>Square</option><option>Ragadan</option><option>Laurentine</option></select></label>
    </header>
    <div className={`perfadom-v50-banner ${lateCount ? "late" : "ok"}`}>
      <strong>{lateCount ? `⚠ ${lateCount} dossier${lateCount > 1 ? "s" : ""} à facturer` : "✓ Tous les dossiers sont à jour"}</strong>
      <span>{active.length} dossier{active.length > 1 ? "s" : ""} en cours</span>
    </div>
    <div className="perfadom-v50-dossiers">
      {items.length ? items.map((item) => {
        const weeks = dossierWeeks(item.startDate, item.endDate);
        const bill = item.payload ? calculateBilling(item.payload.products, item.payload) : null;
        const firstTotal = bill?.all.reduce((sum, line) => sum + line.amount, 0) ?? 0;
        const recurringTotal = bill ? [...bill.followup, ...bill.consumables, ...(bill.act ?? [])].reduce((sum, line) => sum + line.amount, 0) : 0;
        return <article className={item.status === "closed" ? "closed" : ""} key={item.id}>
          <div className="perfadom-v50-dossier-head">
            <span className="perfadom-track-icon">{item.reservedEquipmentId ? "⚙️" : "🩺"}</span>
            <div><strong>{item.patientName || item.reference}</strong><small>{item.reference} · {item.pharmacy} · {item.administrationMode || "mode non renseigné"}</small></div>
            <span className={`perfadom-v50-status ${item.status}`}>{item.status === "closed" ? "Clôturé" : `${item.weeklyPerfusions} / semaine`}</span>
          </div>
          <div className="perfadom-v50-meta"><span>📅 {formatDate(item.startDate)} → {formatDate(item.endDate)}</span><span>👩‍⚕️ {item.ideName || "IDE non renseignée"}</span><span>{item.reservedEquipmentId ? "⚙️ Matériel attribué" : "Sans matériel attribué"}</span></div>
          {weeks.length > 0 && <div className="perfadom-week-plan">
            {weeks.map((week, index) => {
              const invoiced = Boolean(item.invoiceStatus[week]);
              const overdue = week <= today && !invoiced && item.status !== "closed";
              return <div className={invoiced ? "done" : overdue ? "overdue" : "future"} key={week}>
                <p><b>Semaine {index + 1}</b><small>du {formatDate(week)}</small></p>
                <strong>{(index === 0 ? firstTotal : recurringTotal).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</strong>
                <button disabled={item.status === "closed"} onClick={() => void onUpdate(item, "toggle_week", { weekKey: week, invoiced: !invoiced })}>{invoiced ? "↩ Repasser à facturer" : "✓ J’ai facturé"}</button>
              </div>;
            })}
          </div>}
          <footer>
            <button onClick={() => onOpen(item)}>Ouvrir / modifier</button>
            <button onClick={() => onDuplicate(item)}>Dupliquer / renouveler</button>
            <label>Transférer <select value={item.pharmacy} onChange={(event) => void onUpdate(item, "reassign", { pharmacy: event.target.value })}><option>Square</option><option>Ragadan</option><option>Laurentine</option></select></label>
            <button onClick={() => void onUpdate(item, item.status === "closed" ? "reopen" : "close")}>{item.status === "closed" ? "Réouvrir" : "Clôturer"}</button>
            <button className="danger" onClick={() => void onDelete(item)}>Supprimer</button>
          </footer>
        </article>;
      }) : <div className="perfadom-tracking-empty">Aucun dossier pour cette pharmacie.</div>}
    </div>
  </section>;
}

export function PerfadomModule({
  onBack,
  initialPharmacy,
  initialOperator,
  operators,
}: {
  onBack: () => void;
  onOpenMad: () => void;
  initialPharmacy: string;
  initialOperator: string;
  operators: OperatorOption[];
}) {
  const [form, setForm] = useState<PerfadomForm>(() => blankForm(initialPharmacy, initialOperator));
  const [equipment, setEquipment] = useState<MadEquipment[]>([]);
  const [tracking, setTracking] = useState<TrackingItem[]>([]);
  const [tab, setTab] = useState<"create" | "tracking" | "sync">("create");
  const [trackingPharmacy, setTrackingPharmacy] = useState(initialPharmacy || "Square");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const billing = useMemo(() => calculateBilling(form.products, form), [form]);
  const materials = useMemo(() => calculateMaterials(form.products, form), [form]);
  const activeProducts = form.products.filter((item) => item.productName?.trim() || item.description.trim());
  const totalWeekly = activeProducts.reduce((sum, item) => sum + weeklyCount(item), 0);
  const needsPump = activeProducts.some((item) => item.mode === "electric" || item.mode === "diffuser");
  const availablePumps = equipment.filter((item) =>
    item.status === "available" && item.pharmacy === form.pharmacy &&
    /androne|pousse.?seringue|pompe.*perfusion|systeme actif/i.test(`${item.name} ${item.category}`),
  );
  const selectedPump = availablePumps.find((item) => item.id === form.equipmentId);
  const currentTracking = tracking.find((item) => item.reference === form.reference);
  const visibleTracking = tracking.filter((item) => trackingPharmacy === "Toutes" || item.pharmacy === trackingPharmacy);

  const flash = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(""), 3500);
  };
  const update = <K extends keyof PerfadomForm>(key: K, value: PerfadomForm[K]) => setForm((current) => ({ ...current, [key]: value }));
  const updateProduct = <K extends keyof PerfadomProduct>(index: number, key: K, value: PerfadomProduct[K]) => {
    setForm((current) => ({ ...current, products: current.products.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item) }));
  };
  const updatePatientIdentity = (value: string) => {
    const [lastName = "", ...firstName] = value.trimStart().split(/\s+/);
    setForm((current) => ({ ...current, patientLastName: lastName.toLocaleUpperCase("fr"), patientFirstName: firstName.join(" ") }));
  };
  const updateAllProducts = (patch: Partial<PerfadomProduct>) => setForm((current) => ({
    ...current,
    products: current.products.map((product) => ({ ...product, ...patch })),
  }));
  const setTreatmentStart = (startDate: string) => setForm((current) => ({
    ...current,
    products: current.products.map((product) => ({ ...product, startDate, endDate: addDaysIso(startDate, Math.max(1, Number(product.durationDays) || 7) - 1) })),
  }));
  const setTreatmentDuration = (days: number) => setForm((current) => ({
    ...current,
    products: current.products.map((product) => ({ ...product, durationDays: String(days), endDate: addDaysIso(product.startDate || todayIso(), Math.max(1, days) - 1) })),
  }));

  const load = async () => {
    try {
      const [madResponse, trackingResponse] = await Promise.all([
        fetch("/api/mad-rentals", { cache: "no-store" }),
        fetch("/api/perfadom", { cache: "no-store" }),
      ]);
      if (madResponse.ok) {
        const payload = await madResponse.json();
        setEquipment((payload.equipment ?? []).map((row: Record<string, unknown>) => ({
          id: Number(row.id), name: String(row.name ?? ""), category: String(row.category ?? ""),
          pharmacy: String(row.pharmacy ?? ""), inventoryCode: String(row.inventory_code ?? ""),
          serialNumber: String(row.serial_number ?? ""), status: String(row.status ?? ""),
        })));
      }
      if (trackingResponse.ok) {
        const payload = await trackingResponse.json();
        setTracking((payload.items ?? []).map(normalizeTracking));
      }
    } catch {
      flash("Le suivi partagé est indisponible, mais le CERFA reste générable.");
    }
  };

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (!needsPump && (form.allocatePump || form.equipmentId)) {
      setForm((current) => ({ ...current, allocatePump: false, equipmentId: 0 }));
      return;
    }
    if (needsPump && form.allocatePump && !form.equipmentId && availablePumps[0]) update("equipmentId", availablePumps[0].id);
  }, [needsPump, form.allocatePump, form.equipmentId, availablePumps.length]);

  const downloadCerfa = async () => {
    if (!form.patientLastName || !form.patientFirstName || !activeProducts.length) {
      flash("Renseignez au minimum le patient et un produit à perfuser.");
      return;
    }
    setBusy(true);
    try {
      const bytes = await buildPerfadomPdf(form);
      const blob = new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `CERFA-perfusion-domicile-${(form.patientLastName || form.patientFirstName || "patient").replace(/[^a-z0-9_-]+/gi, "_")}.pdf`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
      flash("PDF généré : champs, chiffres et cases alignés sur la grille officielle. À faire relire et signer par le médecin.");
    } catch {
      flash("Le PDF n’a pas pu être généré.");
    } finally {
      setBusy(false);
    }
  };

  const copySummary = async () => {
    const lines = [
      `${form.reference} — ${form.patientLastName.toUpperCase()} ${form.patientFirstName}`.trim(),
      ...activeProducts.map((product, index) => `${index + 1}. ${product.description} — ${weeklyCount(product)}/semaine — ${formatDate(product.startDate)} au ${formatDate(product.endDate)}`),
      `IDE : ${form.ideName || "non renseignée"}${form.idePhone ? ` — ${form.idePhone}` : ""}`,
      ...billing.all.map((line) => `${line.code} — ${line.label} — ${line.amount.toFixed(2)} €`),
    ];
    await navigator.clipboard.writeText(lines.join("\n"));
    flash("Synthèse PERFADOM copiée.");
  };

  const copyMaterials = async () => {
    const lines = [
      "Matériel PERFADOM à fournir",
      `${form.patientLastName || form.patientFirstName ? `Patient : ${form.patientLastName.toUpperCase()} ${form.patientFirstName}` : ""}`,
      form.ideName ? `IDE : ${form.ideName}` : "",
      form.allocatePump ? "Pousse-seringue Androne : affecté à l’IDE" : "",
      `Dispositif : ${activeModeLabel(form.products) || "non renseigné"}`,
      `Perfusions par semaine : ${totalWeekly}`,
      ...activeProducts.map((product) => `- ${product.productName || "Produit"} ${product.volume || ""} · ${product.moment || ""} · ${weeklyCount(product)} / semaine`),
      "",
      ...materials.map((item) => `- ${item.qty} × ${item.title} — ${item.note}${item.lgpi ? `\n  LGPI : ${item.lgpi}` : ""}`),
    ].filter(Boolean);
    await navigator.clipboard.writeText(lines.join("\n")); flash("Matériel PERFADOM copié.");
  };

  const downloadMaterialsChecklist = async () => {
    if (!materials.length) { flash("Ajoutez une ligne de perfusion pour calculer le matériel."); return; }
    setBusy(true);
    try {
      const bytes = await buildMaterialsChecklistPdf(form, materials);
      const blob = new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `Checklist-materiel-${form.reference}.pdf`;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
      flash("Checklist PDF générée pour la préparation et le double contrôle.");
    } catch {
      flash("La checklist PDF n’a pas pu être générée.");
    } finally {
      setBusy(false);
    }
  };

  const saveTracking = async () => {
    if (!form.ideName && form.allocatePump) {
      flash("Indiquez l’IDE avant d’attribuer un pousse-seringue.");
      return;
    }
    setBusy(true);
    try {
      let reservedEquipmentId = currentTracking?.reservedEquipmentId ?? 0;
      if (form.allocatePump && selectedPump && !reservedEquipmentId) {
        const rentalResponse = await fetch("/api/mad-rentals", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "rental",
            equipmentId: selectedPump.id,
            patientName: `IDE ${form.ideName}`,
            phone: form.idePhone,
            startDate: activeProducts[0]?.startDate || todayIso(),
            expectedReturnDate: activeProducts.map((item) => item.endDate).filter(Boolean).sort().at(-1) || addDaysIso(todayIso(), 7),
            prescriptionStatus: "not_required",
            prescriber: `${form.prescriberFirstName} ${form.prescriberLastName}`.trim(),
            assignedBy: form.operator,
            notes: `${form.reference} - matériel mis à disposition de l’IDE. Aucune identité patient enregistrée.`,
          }),
        });
        const rentalPayload = await rentalResponse.json();
        if (!rentalResponse.ok) throw new Error(rentalPayload.error || "Le matériel n’a pas pu être attribué.");
        reservedEquipmentId = selectedPump.id;
      }
      const trackingPayload = {
        reference: form.reference,
        pharmacy: form.pharmacy,
        operator: form.operator,
        status: "active",
        patientName: `${form.patientLastName.toUpperCase()} ${form.patientFirstName}`.trim(),
        prescriptionDate: form.prescriptionDate,
        startDate: activeProducts.map((item) => item.startDate).filter(Boolean).sort()[0] || "",
        endDate: activeProducts.map((item) => item.endDate).filter(Boolean).sort().at(-1) || "",
        durationDays: Math.max(...activeProducts.map((item) => Number(item.durationDays) || 0), 0),
        administrationMode: activeModeLabel(form.products),
        weeklyPerfusions: totalWeekly,
        ideName: form.ideName,
        ideEmail: form.ideEmail,
        reservedEquipmentId,
        payload: form,
        invoiceStatus: currentTracking?.invoiceStatus ?? {},
      };
      const response = await fetch("/api/perfadom", {
        method: currentTracking ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(currentTracking
          ? { ...trackingPayload, id: currentTracking.id, action: "save" }
          : trackingPayload),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || "Le suivi n’a pas pu être enregistré.");
      await load();
      flash(reservedEquipmentId ? "Dossier suivi et pousse-seringue attribué à l’IDE." : "Dossier ajouté au suivi partagé.");
      setTab("tracking");
    } catch (error) {
      flash(error instanceof Error ? error.message : "L’enregistrement a échoué.");
    } finally {
      setBusy(false);
    }
  };

  const updateTracking = async (item: TrackingItem, action: "close" | "reopen" | "toggle_week" | "reassign", extra: Record<string, unknown> = {}) => {
    const response = await fetch("/api/perfadom", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: item.id, action, ...extra }),
    });
    if (response.ok) { await load(); flash(action === "toggle_week" ? "Facturation mise à jour." : "Dossier PERFADOM mis à jour."); }
    else flash("La mise à jour du dossier a échoué.");
  };

  const deleteTracking = async (item: TrackingItem) => {
    if (!window.confirm(`Supprimer définitivement le dossier ${item.reference} ?`)) return;
    const response = await fetch(`/api/perfadom?id=${item.id}`, { method: "DELETE" });
    if (response.ok) { await load(); flash("Dossier supprimé."); }
  };

  const openTracking = (item: TrackingItem) => {
    setForm(trackingForm(item));
    setTab("create");
    flash(item.payload ? "Dossier ouvert pour modification." : "Dossier historique repris avec les informations disponibles.");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const duplicateTracking = (item: TrackingItem) => {
    setForm(trackingForm(item, true));
    setTab("create");
    flash(`Copie créée depuis ${item.reference}. Modifiez-la puis enregistrez le nouveau dossier.`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const completeness = [
    { label: "Patient", ok: Boolean(form.patientLastName && form.patientFirstName && form.birthDate) },
    { label: "N° assuré", ok: Boolean(form.insuredNumber) },
    { label: "Prescripteur", ok: Boolean(form.prescriberLastName && form.rpps) },
    { label: "Structure", ok: Boolean(form.structure && (form.finess || form.assuranceMaladie)) },
    { label: "Traitement", ok: activeProducts.length > 0 },
    { label: "Dates et fréquence", ok: activeProducts.every((item) => item.startDate && item.endDate && item.frequencyCount) },
    { label: "IDE", ok: Boolean(form.ideName) },
  ];

  return (
    <main className="perfadom-app perfadom-classic perfadom-original">
      <header className="module-header perfadom-header">
        <button className="back-button" onClick={onBack}>← Accueil</button>
        <div className="module-brand"><span className="brand-mark">sp</span><div><strong>saint-laurent</strong><small>PHARMACIES</small></div></div>
        <div className="perfadom-header-actions"><a href="/?tools=1">▦ Applications</a><span><i />Calcul local sécurisé</span></div>
      </header>

      <section className="perfadom-classic-title">
        <div><span>AIDE COMPTOIR / MAD</span><h1>Facturation PERFADOM</h1><p>Assistant de cure : saisissez le patient, l’ordonnance et les lignes de perfusion. Le portail calcule les perfusions, les forfaits semaine par semaine, le matériel et le suivi des dossiers.</p></div>
        <div className="perfadom-original-warning">⚠️ Aide interne. Toujours vérifier l’ordonnance, la fréquence prescrite et les règles LPPR en vigueur avant facturation.</div>
        <div className="perfadom-classic-title-actions">
          <button onClick={() => setForm(blankForm(form.pharmacy, form.operator))}>＋ Nouveau dossier</button>
          <button className="primary" disabled={busy} onClick={downloadCerfa}>📄 Générer le formulaire</button>
        </div>
      </section>

      {notice && <div className="perfadom-notice">{notice}</div>}

      <nav className="perfadom-v50-tabs" aria-label="Navigation PERFADOM">
        <button className={tab === "create" ? "active" : ""} onClick={() => setTab("create")}>➕ Créer un dossier</button>
        <button className={tab === "tracking" ? "active" : ""} onClick={() => setTab("tracking")}>📋 Suivre mes dossiers <b>{tracking.filter((item) => item.status !== "closed").length}</b></button>
        <button className={tab === "sync" ? "active" : ""} onClick={() => setTab("sync")}>🔄 Tester synchro</button>
      </nav>

      {tab === "create" && <>
      <div className="perfadom-owner-badge">🏥 Dossier rattaché automatiquement à : <strong>{form.pharmacy}</strong></div>
      <div className="perfadom-classic-layout">
        <div className="perfadom-classic-main">
          <section className="perfadom-classic-panel perfadom-prescription-overview">
            <header><b>1</b><div><h2>Patient & ordonnance</h2></div></header>
            <div className="perfadom-overview-grid">
              <label className="patient"><span>Patient</span><input value={[form.patientLastName, form.patientFirstName].filter(Boolean).join(" ")} onChange={(event) => updatePatientIdentity(event.target.value)} placeholder="Nom / prénom ou initiales" /></label>
              <label><span>Date ordonnance</span><div className="perfadom-inline-date"><input type="date" value={form.prescriptionDate} onChange={(event) => update("prescriptionDate", event.target.value)} /><button type="button" onClick={() => update("prescriptionDate", todayIso())}>Auj.</button><button type="button" onClick={() => update("prescriptionDate", addDaysIso(todayIso(), -1))}>Hier</button></div></label>
              <label><span>Début traitement</span><input type="date" value={form.products[0]?.startDate || todayIso()} onChange={(event) => setTreatmentStart(event.target.value)} /></label>
              <label className="products"><span>Produits à diffuser + posologies</span><textarea value={form.comments} onChange={(event) => update("comments", event.target.value)} placeholder="Ex : NaCl 500 ml 1 jour sur 2 la nuit ; alternance NaCl / Glucose ; diffuseur 100 ml nuit…" /></label>
              <div className="perfadom-duration"><span>Durée totale ordonnance</span><div><input inputMode="numeric" value={form.products[0]?.durationDays || "7"} onChange={(event) => setTreatmentDuration(Math.max(1, Number(event.target.value) || 1))} /><b>jours</b><button type="button" onClick={() => setTreatmentDuration(7)}>7 j</button><button type="button" onClick={() => setTreatmentDuration(14)}>2 sem.</button><button type="button" onClick={() => setTreatmentDuration(30)}>1 mois</button><button type="button" onClick={() => setTreatmentDuration(90)}>3 mois</button></div></div>
              <label className="device"><span>Dispositif principal</span><select value={form.products[0]?.mode || "diffuser"} onChange={(event) => updateAllProducts({ mode: event.target.value as PerfadomProduct["mode"] })}><option value="diffuser">Diffuseur</option><option value="gravity">Gravité</option><option value="electric">Système actif électrique</option></select></label>
            </div>
          </section>

          <section className="perfadom-classic-panel">
            <header className="perfadom-line-heading"><b>2</b><div><h2>Lignes de perfusion</h2><p>Une ligne = un produit à diffuser. Le nombre de diffuseurs/perfusions est calculé automatiquement.</p></div></header>
            <div className="perfadom-classic-products">
              {form.products.map((product, index) => (
                <article className="perfadom-perfusion-line" key={index}>
                  <div className="perfadom-original-rx-grid">
                    <label><span>Produit</span><select value={product.productName} onChange={(event) => updateProduct(index, "productName", event.target.value)}><option>NaCl</option><option>Glucose</option><option>Bionolyte</option><option>Autre</option></select></label>
                    <label><span>Volume</span><select value={product.volume} onChange={(event) => updateProduct(index, "volume", event.target.value as PerfadomProduct["volume"])}><option>50 ml</option><option>100 ml</option><option>250 ml</option><option>500 ml</option><option>1000 ml</option></select></label>
                    <label className="duration"><span>Durée diffusion</span><div><input inputMode="numeric" value={product.durationHours} onChange={(event) => updateProduct(index, "durationHours", event.target.value)} placeholder="h" /><small>heure(s)</small></div><span className="perfadom-duration-shortcuts">{[6, 12, 18, 24].map((hours) => <button type="button" key={hours} onClick={() => updateProduct(index, "durationHours", String(hours))}>{hours} h</button>)}</span></label>
                    <label><span>Moment</span><input value={product.moment} onChange={(event) => updateProduct(index, "moment", event.target.value)} placeholder="nuit / journée" /></label>
                    <label><span>Rythme</span><select value={product.schedule} onChange={(event) => updateProduct(index, "schedule", event.target.value as PerfadomProduct["schedule"])}><option value="daily">Tous les jours</option><option value="every2A">1 jour sur 2 — jour A</option><option value="every2B">1 jour sur 2 — jour B / alternance</option><option value="manual">Selon commentaire ordonnance</option></select></label>
                    {form.products.length > 1 && <button type="button" className="perfadom-remove-line" onClick={() => update("products", form.products.filter((_, itemIndex) => itemIndex !== index))}>Retirer</button>}
                  </div>
                  <details className="perfadom-line-details"><summary>Réglages techniques de la ligne</summary><label className="full"><span>Dosage, posologie, débit et solvant</span><textarea value={product.description} onChange={(event) => updateProduct(index, "description", event.target.value)} placeholder="Précisions de l’ordonnance…" /></label><div className="perfadom-classic-choices">
                    <fieldset><legend>Voie d’abord</legend>{[["central", "Centrale"], ["peripheral", "Périphérique"], ["subcutaneous", "Sous-cutanée"], ["perineural", "Péri-nerveuse"]].map(([value, label]) => <label className={product.route === value ? "selected" : ""} key={value}><input type="radio" checked={product.route === value} onChange={() => updateProduct(index, "route", value as PerfadomProduct["route"])} />{label}</label>)}</fieldset>
                    <fieldset><legend>Mode</legend>{[["gravity", "💧 Gravité"], ["diffuser", "🫧 Diffuseur"], ["electric", "⚡ Système actif"]].map(([value, label]) => <label className={product.mode === value ? "selected" : ""} key={value}><input type="radio" checked={product.mode === value} onChange={() => updateProduct(index, "mode", value as PerfadomProduct["mode"])} />{label}</label>)}</fieldset>
                    <fieldset><legend>Précisions</legend>
                      <label className={product.hospitalFilled ? "selected" : ""}><input type="checkbox" checked={product.hospitalFilled} onChange={(event) => updateProduct(index, "hospitalFilled", event.target.checked)} />Rempli à l’hôpital</label>
                      <label className={product.alone ? "selected" : ""}><input type="checkbox" checked={product.alone} onChange={(event) => updateProduct(index, "alone", event.target.checked)} />À perfuser seul</label>
                      <label className={product.maintenance ? "selected" : ""}><input type="checkbox" checked={product.maintenance} onChange={(event) => updateProduct(index, "maintenance", event.target.checked)} />Entretien intercure</label>
                    </fieldset>
                  </div></details>
                </article>
              ))}
            </div>
            <div className="perfadom-classic-add">
              <button disabled={form.products.length >= 2} onClick={() => update("products", [...form.products, emptyProduct()])}>＋ Ajouter une ligne de perfusion</button>
              <button disabled={form.products.length >= 2} onClick={() => update("products", [{ ...emptyProduct(), productName: "NaCl", schedule: "every2A" }, { ...emptyProduct(), productName: "Glucose", schedule: "every2B" }])}>＋ Alternance NaCl / Glucose</button>
              <strong>{totalWeekly} perfusion{totalWeekly > 1 ? "s" : ""} / semaine</strong>
            </div>
          </section>

          <details className="perfadom-classic-cerfa perfadom-cerfa-details">
            <summary className="perfadom-cerfa-copy"><span>2 bis</span><div><h2>Formulaire de prescription CERFA</h2><p>Les informations administratives du patient et du médecin sont regroupées ici.</p></div><b className="perfadom-cerfa-toggle">Déplier</b></summary>
            <div className="perfadom-cerfa-admin-grid">
              <section><header><b>1</b><h3>Patient et prescription</h3></header><div className="perfadom-classic-grid four"><label><span>Nom patient *</span><input value={form.patientLastName} onChange={(event) => update("patientLastName", event.target.value)} /></label><label><span>Prénom patient *</span><input value={form.patientFirstName} onChange={(event) => update("patientFirstName", event.target.value)} /></label><label><span>Date de naissance</span><input type="date" value={form.birthDate} onChange={(event) => update("birthDate", event.target.value)} /></label><label><span>Poids (kg)</span><input value={form.weight} onChange={(event) => update("weight", event.target.value)} /></label><label className="span-two"><span>N° assuré</span><input value={form.insuredNumber} onChange={(event) => update("insuredNumber", event.target.value.replace(/\s/g, ""))} placeholder="15 chiffres" /></label><label><span>Type de prescription</span><select value={form.prescriptionType} onChange={(event) => update("prescriptionType", event.target.value as PerfadomForm["prescriptionType"])}><option value="initiation">Initiation</option><option value="renewal">Renouvellement</option></select></label><label className="perfadom-classic-check"><input type="checkbox" checked={form.ald} onChange={(event) => update("ald", event.target.checked)} /><span>Soins en rapport avec une ALD</span></label></div></section>
              <section><header><b>2</b><h3>Médecin prescripteur</h3></header><div className="perfadom-classic-grid four"><label><span>Nom médecin *</span><input value={form.prescriberLastName} onChange={(event) => update("prescriberLastName", event.target.value)} /></label><label><span>Prénom médecin</span><input value={form.prescriberFirstName} onChange={(event) => update("prescriberFirstName", event.target.value)} /></label><label><span>Téléphone</span><input value={form.prescriberPhone} onChange={(event) => update("prescriberPhone", event.target.value)} /></label><label><span>RPPS *</span><input value={form.rpps} onChange={(event) => update("rpps", event.target.value.replace(/\s/g, ""))} /></label><label><span>N° FINESS</span><input value={form.finess} onChange={(event) => update("finess", event.target.value)} /></label><label><span>N° AM</span><input value={form.assuranceMaladie} onChange={(event) => update("assuranceMaladie", event.target.value)} /></label><label><span>Structure / établissement</span><input value={form.structure} onChange={(event) => update("structure", event.target.value)} /></label><label><span>Cadre</span><select value={form.careSetting} onChange={(event) => { const careSetting = event.target.value as PerfadomForm["careSetting"]; setForm((current) => ({ ...current, careSetting, recipientHospital: careSetting === "had" })); }}><option value="city">Perfusion en ville</option><option value="had">Hospitalisation à domicile</option></select></label><label className="span-three"><span>Adresse de la structure + code postal et ville</span><input value={form.structureAddress} onChange={(event) => update("structureAddress", event.target.value)} /></label><label><span>Préparé par</span><select value={form.operator} onChange={(event) => update("operator", event.target.value)}>{operators.filter((item) => item.pharmacy === form.pharmacy).map((item) => <option key={item.name}>{item.name}</option>)}</select></label></div></section>
            </div>
            <section className="perfadom-cerfa-recipients"><header><b>3</b><h3>Destinataire / objet</h3></header><div>{([
              ["recipientPatient", "1 — Patient"], ["recipientProducts", "2.1 — Produit(s) à perfuser"], ["recipientServices", "2.2 — Prestations / dispositifs"], ["recipientIde", "2.3 — Copie pour l’IDE"], ["recipientHospital", "3 — Hôpital / HAD"],
            ] as const).map(([key, label]) => <label className={form[key] ? "selected" : ""} key={key}><input type="checkbox" checked={form[key]} onChange={(event) => update(key, event.target.checked)} /><span>{label}</span></label>)}</div></section>
            <div className="perfadom-cerfa-line-count"><div><span>Lignes de perfusion</span><strong>{form.products.length}</strong></div><p>{form.products.length === 1 ? "1 ligne reprise automatiquement du cadre 2" : `${form.products.length} lignes reprises automatiquement du cadre 2`}</p></div>
            <div className="perfadom-cerfa-products">{form.products.slice(0, 2).map((product, index) => <section key={index} className={index === 0 ? "primary" : "optional"}>
              <header><b>{index + 1}</b><h3>Produit à perfuser n°{index + 1}</h3>{index === 1 && <small>facultatif</small>}<em>Repris automatiquement du cadre 2</em></header>
              <label className="full"><span>Produit / dosage / posologie / solvant</span><textarea readOnly value={[product.productName, product.volume, product.description, product.moment].filter(Boolean).join(" — ")} placeholder={index === 1 ? "Laisser vide si un seul produit" : "Produit à perfuser"} /></label>
              <div className="perfadom-cerfa-product-grid"><label><span>Durée d’une perfusion</span><input readOnly value={product.durationHours ? `${product.durationHours} h${product.durationMinutes ? ` ${product.durationMinutes} min` : ""}` : ""} placeholder="Ex : 12 h" /></label><label><span>Nombre total de perfusions</span><input readOnly value={cerfaTotalPerfusions(product)} /></label><label><span>Fréquence</span><input readOnly value={cerfaFrequency(product)} /></label><label><span>Début cure</span><input type="date" value={product.startDate} onChange={(event) => updateProduct(index, "startDate", event.target.value)} /></label><label><span>Fin cure</span><input type="date" value={product.endDate} onChange={(event) => updateProduct(index, "endDate", event.target.value)} /></label><label><span>Durée cure (jours)</span><input inputMode="numeric" value={product.durationDays} onChange={(event) => updateProduct(index, "durationDays", event.target.value)} /></label><label><span>Voie d’abord</span><select value={product.route} onChange={(event) => updateProduct(index, "route", event.target.value as PerfadomProduct["route"])}><option value="peripheral">Veineuse périphérique</option><option value="central">Veineuse centrale</option><option value="subcutaneous">Sous-cutanée</option><option value="perineural">Péri-nerveuse</option></select></label><label><span>Mode d’administration</span><select value={product.mode} onChange={(event) => updateProduct(index, "mode", event.target.value as PerfadomProduct["mode"])}><option value="diffuser">Diffuseur</option><option value="gravity">Gravité</option><option value="electric">Système actif électrique</option></select></label><label className="perfadom-cerfa-alone"><input type="checkbox" checked={product.alone} onChange={(event) => updateProduct(index, "alone", event.target.checked)} /><span>Ce produit est perfusé seul</span></label></div>
            </section>)}</div>
            <section className="perfadom-cerfa-other"><header><b>4</b><h3>Autres cures / commentaires</h3></header><div><label className={form.priorCure26Weeks === "yes" ? "selected" : ""}><input type="checkbox" checked={form.priorCure26Weeks === "yes"} onChange={(event) => update("priorCure26Weeks", event.target.checked ? "yes" : "no")} /><span>Oui — cure ville / NPAD dans les 26 dernières semaines</span></label><label className={form.currentCure === "yes" ? "selected" : ""}><input type="checkbox" checked={form.currentCure === "yes"} onChange={(event) => update("currentCure", event.target.checked ? "yes" : "no")} /><span>Oui — cure ville / NPAD actuellement en cours</span></label></div><label><span>Commentaire prescripteur</span><textarea value={form.comments} onChange={(event) => update("comments", event.target.value)} placeholder="Matériel indispensable, schéma thérapeutique, particularités…" /></label></section>
            <div className="perfadom-checklist">{completeness.map((item) => <div className={item.ok ? "ok" : "missing"} key={item.label}><span>{item.ok ? "✓" : "!"}</span><b>{item.label}</b></div>)}</div>
            <div className="perfadom-classic-cerfa-actions">
              <button className="primary" disabled={busy} onClick={downloadCerfa}>{busy ? "Génération…" : "📄 Préremplir depuis les cartes 1, 2 et 3"}</button>
              <a href="/docs/formulaire/perfusion-domicile-perfadom-ordo.pdf" target="_blank" rel="noreferrer">PDF vierge</a>
              <button onClick={copySummary}>Copier la synthèse</button>
            </div>
          </details>
          <div className="perfadom-original-actionbar">
            <button type="button" onClick={() => setForm(blankForm(form.pharmacy, form.operator))}>Réinitialiser</button>
            <button type="button" disabled={busy} onClick={saveTracking}>Enregistrer dossier</button>
            <button type="button" className="primary" onClick={copySummary}>Copier synthèse</button>
          </div>
        </div>

        <aside className="perfadom-classic-side">
          <section className="perfadom-classic-summary">
            <span>CALCUL EN DIRECT</span><strong>{totalWeekly}</strong><p>perfusion{totalWeekly > 1 ? "s" : ""} / semaine</p>
            <small>{activeModeLabel(form.products) || "Ajoutez une ligne de perfusion"}</small>
          </section>

          <section className="perfadom-classic-side-card">
            <header><h2>3. Matériel</h2><div className="perfadom-material-actions"><button type="button" onClick={copyMaterials}>Copier</button><button type="button" disabled={busy} onClick={downloadMaterialsChecklist}>📄 PDF checklist</button></div></header>
            <div className="perfadom-material-options">
              <label><input type="checkbox" checked={form.installation} onChange={(event) => update("installation", event.target.checked)} /> Installation à facturer</label>
              <label><input type="checkbox" checked={form.hospitalPrepared} onChange={(event) => update("hospitalPrepared", event.target.checked)} /> Préparé / posé à l’hôpital</label>
              <label><input type="checkbox" checked={form.threeWayExtension} onChange={(event) => update("threeWayExtension", event.target.checked)} /> Prolongateur 3 voies</label>
              <label><span>Cathéter</span><select value={form.catheterType} onChange={(event) => update("catheterType", event.target.value as PerfadomForm["catheterType"])}><option value="blue">Bleu + prolongateur séparé</option><option value="yellow">Jaune avec prolongateur intégré</option></select></label>
            </div>
            <details className="perfadom-ide-details">
              <summary><span>👩‍⚕️</span><div><strong>IDE</strong><small>{form.ideName || "Coordonnées à renseigner si nécessaire"}</small></div><b><i>Déplier</i><em>Replier</em></b></summary>
              <div className="perfadom-ide-grid">
                <label><span>IDE {form.allocatePump ? "*" : ""}</span><input value={form.ideName} onChange={(event) => update("ideName", event.target.value)} placeholder="Nom complet" /></label>
                <label><span>Téléphone</span><input value={form.idePhone} onChange={(event) => update("idePhone", event.target.value)} /></label>
                <label className="full"><span>E-mail IDE</span><input type="email" value={form.ideEmail} onChange={(event) => update("ideEmail", event.target.value)} placeholder="ide@cabinet.fr" /></label>
                <label className="full"><span>Adresse / cabinet</span><input value={form.ideAddress} onChange={(event) => update("ideAddress", event.target.value)} /></label>
              </div>
            </details>
            {needsPump ? (
              <div className={`perfadom-stock-match ${availablePumps.length ? "available" : "missing"}`}>
                <div><span>{availablePumps.length ? "✓" : "!"}</span><p><strong>{availablePumps.length ? `${availablePumps.length} pompe${availablePumps.length > 1 ? "s" : ""} Androne disponible${availablePumps.length > 1 ? "s" : ""}` : "Aucune pompe Androne disponible"}</strong><small>{form.pharmacy} · Stock lu automatiquement depuis le parc MAD</small></p></div>
                {availablePumps.length > 0 && <div className="perfadom-pump-allocation">
                  <label className="allocation-toggle"><input type="checkbox" checked={form.allocatePump} onChange={(event) => { const checked = event.target.checked; setForm((current) => ({ ...current, allocatePump: checked, equipmentId: checked ? (current.equipmentId || availablePumps[0]?.id || 0) : 0 })); }} /><span>Attribuer une pompe à cet IDE / dossier patient</span></label>
                  {form.allocatePump && <label className="perfadom-pump-picker"><span>Pompe à attribuer</span><select value={form.equipmentId} onChange={(event) => update("equipmentId", Number(event.target.value))}>{availablePumps.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.inventoryCode || item.serialNumber || `n°${item.id}`}</option>)}</select></label>}
                </div>}
              </div>
            ) : <div className="perfadom-stock-match neutral"><div><span>i</span><p><strong>Aucun système actif demandé</strong><small>Le parc MAD n’est pas mobilisé.</small></p></div></div>}
            {needsPump && <div className="perfadom-tutorial-actions">
              <a href="https://www.youtube.com/watch?v=VkL30pybs1Q" target="_blank" rel="noreferrer">▶ Voir le tutoriel Androne</a>
              <a href={`mailto:${encodeURIComponent(form.ideEmail)}?subject=${encodeURIComponent("Tutoriel d’utilisation du pousse-seringue Androne")}&body=${encodeURIComponent(`Bonjour${form.ideName ? ` ${form.ideName}` : ""},\n\nVoici le tutoriel d’utilisation du pousse-seringue Androne :\nhttps://www.youtube.com/watch?v=VkL30pybs1Q\n\nImportant : utiliser des seringues de 20 mL Luer Lock.\n\nCordialement`)}`}>✉ Envoyer à l’IDE</a>
            </div>}
            <div className="perfadom-material-heading"><h3>4. Matériel à fournir</h3><small>Calculé sur toute la durée prescrite</small></div>
            <ul className="perfadom-material-list">{materials.map((item, index) => <li key={`${item.title}-${index}`}><span>{item.qty}</span><p><strong>{item.title}<em>{item.tag}</em></strong><small>{item.note}</small>{item.lgpi && <small className="perfadom-lgpi-name"><b>LGPI</b>{item.lgpi}</small>}</p></li>)}</ul>
          </section>

          <section className="perfadom-classic-side-card">
            <header><h2>5. Plan global de facturation</h2><span className="perfadom-source-chip">Base fournie 01/2023</span></header>
            <div className="perfadom-billing-list">{billing.all.length ? billing.all.map((line, index) => <div key={`${line.code}-${index}`}><span>{line.code}</span><p><strong>{line.label}</strong><small>{line.frequency}</small></p><b>{line.amount.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</b></div>) : <p className="perfadom-empty-line">Ajoutez une ligne de perfusion pour calculer le plan et le matériel.</p>}</div>
            {billing.all.length > 0 && <div className="perfadom-billing-total"><span>Total première semaine</span><strong>{billing.all.reduce((sum, line) => sum + line.amount, 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €</strong></div>}
            <p className="perfadom-classic-warning">Installation facturable à la délivrance. Consommables et suivi : semaine par semaine, à terme échu. Vérifier les tarifs avant facturation.</p>
            <button className="perfadom-track-button" disabled={busy} onClick={saveTracking}>{currentTracking ? "Enregistrer les modifications" : form.allocatePump ? "Enregistrer + attribuer le matériel" : "Enregistrer dans le suivi"}</button>
          </section>
        </aside>
      </div>
      </>}

      {tab === "tracking" && <PerfadomTrackingView
        items={visibleTracking}
        pharmacy={trackingPharmacy}
        onPharmacy={setTrackingPharmacy}
        onOpen={openTracking}
        onDuplicate={duplicateTracking}
        onUpdate={updateTracking}
        onDelete={deleteTracking}
      />}

      {tab === "sync" && <section className="perfadom-sync-card">
        <span className="perfadom-sync-icon">✓</span>
        <div><h2>Synchro portail opérationnelle</h2><p>{tracking.length} dossier{tracking.length > 1 ? "s" : ""} reçu{tracking.length > 1 ? "s" : ""} depuis la base partagée. Les retards de facturation sont transmis automatiquement à la liste d’actions et à l’écran TV.</p></div>
        <button onClick={() => void load()}>Relancer le test</button>
      </section>}
    </main>
  );
}
